/**
 * Générateur — Calculer le produit de deux nombres décimaux
 * Attendu ID : 613 | Niveau : 6ème | Domaine : NC
 *
 * Types générés :
 *   - calcmult_qcm_unique          (calculer un produit / double / triple d'un décimal)
 *   - calcmult_texte               (saisie du produit)
 *   - calcmult_relier              (relier 4 produits indépendants à leurs résultats)
 *   - calcmult_tableau_vf          (V/F sur des calculs 3V/3F)
 *   - calcmult_texte_trou_drag     (compléter un tableau de produits)
 *
 * Règles dures de la fiche cluster 8 (section ID 613) :
 *   - Facteurs décimaux 2..99 entiers « cachés », décalés de 0 à 2 rangs.
 *   - Résultats lisibles (≤ 4 décimales) et calculables sans calculatrice.
 *   - Inclure des cas où le résultat « retombe » à l'entier ou à 1 décimale.
 *   - Sur un lot de 12 ex : ≥ 3 cas avec retombée, ≥ 1 cas double, ≥ 1 cas triple.
 *
 * Mécanismes-clés testés :
 *   - Pose et calcul du produit d'entiers sous-jacent
 *   - Placement de la virgule (somme des chiffres décimaux des facteurs)
 *   - Double/triple d'un décimal
 *   - Lecture canonique d'un résultat (3,60 → 3,6)
 *
 * Variantes estimées : > 5000
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 613;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_QCM = 'Calculer un produit décimal ou un double/triple d\'un décimal';
const L_SAISIE = 'Taper le résultat d\'un produit décimal';
const L_RELIER = 'Relier des produits décimaux à leurs résultats';
const L_VF = 'Vrai/Faux sur des calculs de produits décimaux';
const L_TROU_DRAG = 'Compléter un tableau de produits décimaux (drag)';

// ─── Helpers numériques (string-based, exact) ───

const decCount = (s) => {
    const [, dec = ''] = String(s).split(',');
    return dec.length;
};

const stripZeros = (s) => {
    if (!s.includes(',')) return s;
    let t = s.replace(/0+$/, '');
    if (t.endsWith(',')) t = t.slice(0, -1);
    return t;
};

/** Produit exact de deux décimaux en écriture française (multiplication entière).
 *  Valide tant que les facteurs strippés restent ≪ Number.MAX_SAFE_INTEGER (~9×10¹⁵). */
const mulDec = (a, b) => {
    const da = decCount(a);
    const db = decCount(b);
    const ai = parseInt(String(a).replace(/[\s,]/g, ''), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, ''), 10);
    const prod = ai * bi;
    const totalDec = da + db;
    if (totalDec === 0) return String(prod);
    let p = String(prod);
    while (p.length <= totalDec) p = '0' + p;
    const intPart = p.slice(0, p.length - totalDec);
    const decPart = p.slice(p.length - totalDec);
    const intTrim = intPart.replace(/^0+/, '') || '0';
    const decTrim = decPart.replace(/0+$/, '');
    if (decTrim === '') return intTrim;
    return `${intTrim},${decTrim}`;
};

/** Décale la virgule de k rangs vers la gauche. */
const shiftLeft = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const newDecs = decPart.length + k;
    const totalLen = allDigits.length;
    if (newDecs < totalLen) {
        const newInt = allDigits.slice(0, totalLen - newDecs);
        const newDec = allDigits.slice(totalLen - newDecs);
        const newIntTrim = newInt.replace(/^0+/, '') || '0';
        const newDecTrim = newDec.replace(/0+$/, '');
        if (newDecTrim === '') return newIntTrim;
        return `${newIntTrim},${newDecTrim}`;
    }
    const zeros = '0'.repeat(newDecs - totalLen);
    const rawDec = zeros + allDigits;
    const decTrim = rawDec.replace(/0+$/, '');
    if (decTrim === '') return '0';
    return `0,${decTrim}`;
};

const sameDecimal = (a, b) => {
    const toNum = (s) => parseFloat(String(s).replace(',', '.'));
    return Math.abs(toNum(a) - toNum(b)) < 1e-12;
};

// ─── Pools de couples d'entiers « cachés » (produits calculables) ───

/**
 * Couples (A, B) d'entiers dont le produit est calculable de tête ou très
 * facilement par un élève de 6e (en posant le calcul mentalement). Le produit
 * entier P est stocké pour gagner du temps et éviter IEEE 754.
 */
const COUPLES_ENTIERS = [
    ['24', '15', '360'], ['12', '14', '168'], ['32', '15', '480'],
    ['42', '13', '546'], ['18', '25', '450'], ['36', '15', '540'],
    ['14', '13', '182'], ['25', '12', '300'], ['16', '25', '400'],
    ['34', '12', '408'], ['28', '15', '420'], ['45', '12', '540'],
    ['21', '14', '294'], ['13', '11', '143'], ['18', '12', '216'],
    ['41', '23', '943'], ['36', '25', '900'], ['52', '15', '780'],
    ['27', '11', '297'], ['24', '13', '312'], ['15', '15', '225'],
    ['12', '25', '300'], ['18', '15', '270'], ['22', '14', '308'],
    ['11', '11', '121'], ['24', '11', '264'], ['18', '11', '198'],
    ['25', '11', '275'], ['22', '15', '330'], ['44', '25', '1100'],
];

/**
 * Décline un couple en écriture décimale : sélectionne des décalages da et db
 * tels que da + db ≥ 2, afin d'obtenir un produit avec au moins 2 décimales
 * (et donc tester le placement de virgule). decA, decB ∈ {0,1,2}.
 */
const randomDecline = (couple, { minDec = 2 } = {}) => {
    const [A, B, P] = couple;
    // Piocher aléatoirement decA et decB avec contraintes
    let tries = 0;
    while (tries < 10) {
        const decA = randInt(0, 2);
        const decB = randInt(0, 2);
        if (decA + decB < minDec) { tries++; continue; }
        const aStr = shiftLeft(A, decA);
        const bStr = shiftLeft(B, decB);
        const resStr = shiftLeft(P, decA + decB);
        return { aStr, bStr, resStr, decTot: decA + decB };
    }
    // Fallback : (1, 1)
    return {
        aStr: shiftLeft(A, 1),
        bStr: shiftLeft(B, 1),
        resStr: shiftLeft(P, 2),
        decTot: 2,
    };
};

// ═══════════════════════════════════════════════════════════
// 1. QCM UNIQUE — Calculer un produit (ou un double/triple)
// ═══════════════════════════════════════════════════════════

/**
 * Rappel affiché dans tous les énoncés : cet attendu teste le CALCUL écrit
 * d'un produit décimal. L'élève doit poser la multiplication au brouillon.
 */
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pense à poser la multiplication sur ton cahier ou au brouillon avant de répondre.</p>';

const FORMULATIONS_QCM_PRODUIT = [
    (a, b) => `${RAPPEL_BROUILLON}<p>Combien fait ${a} × ${b} ?</p>`,
    (a, b) => `${RAPPEL_BROUILLON}<p>Calcule ${a} × ${b}.</p>`,
    (a, b) => `${RAPPEL_BROUILLON}<p>Quel est le produit de ${a} par ${b} ?</p>`,
];

const FORMULATIONS_QCM_DOUBLE = [
    (a) => `${RAPPEL_BROUILLON}<p>Quel est le double de ${a} ?</p>`,
    (a) => `${RAPPEL_BROUILLON}<p>Calcule le double de ${a}.</p>`,
];

const FORMULATIONS_QCM_TRIPLE = [
    (a) => `${RAPPEL_BROUILLON}<p>Quel est le triple de ${a} ?</p>`,
    (a) => `${RAPPEL_BROUILLON}<p>Calcule le triple de ${a}.</p>`,
];

/** Pool de décimaux pour double/triple. */
const POOL_DEC_DOUBLE_TRIPLE = [
    '2,4', '3,7', '4,5', '6,2', '7,8', '8,5', '12,3', '15,4', '21,6', '32,3',
    '5,6', '9,1', '14,2', '18,5', '25,7',
];

// Anti-répétition immédiate (revue 2026-08-20, recette standard).
let derniereSignatureQcm613 = null;

const genCalcMultQcm = () => {
    let payload = construireCalcMultQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureQcm613; t++) {
        payload = construireCalcMultQcm();
    }
    derniereSignatureQcm613 = payload.texte;
    return payload;
};

const construireCalcMultQcm = () => {
    const r = Math.random();

    // 20% : DOUBLE d'un décimal
    if (r < 0.2) {
        const a = randomChoice(POOL_DEC_DOUBLE_TRIPLE);
        const correct = mulDec('2', a);
        const dist = new Set();
        // D1 : décalage trop court
        dist.add(shiftLeft(String(2 * parseInt(a.replace(',', ''), 10)), Math.max(0, decCount(a) - 1)));
        // D2 : addition (a + 2)
        dist.add(mulDec('1', a).replace(/,(\d)$/, (_, d) => `,${(parseInt(d) + 2) % 10}`));  // hack léger
        // D3 : chiffre décimal inchangé (l'élève ne double que l'entier)
        {
            const [ai, ad = ''] = a.split(',');
            const newInt = String(parseInt(ai, 10) * 2);
            dist.add(ad ? `${newInt},${ad}` : newInt);
        }
        // D4 : concaténation / identité
        dist.add(a);

        const arr = [...dist].filter((x) => !sameDecimal(x, correct));
        while (arr.length < 3) arr.push(`${arr.length}`);

        return makeQcm(
            [correct, ...arr.slice(0, 3)],
            correct,
            randomChoice(FORMULATIONS_QCM_DOUBLE)(a),
            opts(L_QCM),
        );
    }

    // 20% : TRIPLE d'un décimal
    if (r < 0.4) {
        const a = randomChoice(POOL_DEC_DOUBLE_TRIPLE);
        const correct = mulDec('3', a);
        const dist = new Set();
        // D1 : sans virgule
        {
            const [ai, ad = ''] = a.split(',');
            const asInt = ai + ad;
            dist.add(String(parseInt(asInt, 10) * 3));
        }
        // D2 : partie décimale oubliée
        {
            const [ai] = a.split(',');
            dist.add(String(parseInt(ai, 10) * 3));
        }
        // D3 : addition (a + 3)
        {
            const [ai, ad = ''] = a.split(',');
            const addInt = parseInt(ai, 10) + 3;
            dist.add(ad ? `${addInt},${ad}` : String(addInt));
        }

        const arr = [...dist].filter((x) => !sameDecimal(x, correct));
        while (arr.length < 3) arr.push(`${arr.length * 10}`);

        return makeQcm(
            [correct, ...arr.slice(0, 3)],
            correct,
            randomChoice(FORMULATIONS_QCM_TRIPLE)(a),
            opts(L_QCM),
        );
    }

    // 60% : produit décimal × décimal
    const couple = randomChoice(COUPLES_ENTIERS);
    const [A, B, P] = couple;
    const v = randomDecline(couple, { minDec: 2 });
    const correct = v.resStr;

    const dist = new Set();
    // D1 : virgule absente (résultat = P entier)
    dist.add(P);
    // D2 : décalage trop court OU trop long (un seul des deux, tiré au sort)
    if (v.decTot >= 1 && randomChoice([true, false])) {
        dist.add(shiftLeft(P, Math.max(0, v.decTot - 1)));
    } else {
        dist.add(shiftLeft(P, v.decTot + 1));
    }
    // D3 : chiffres FAUX, virgule bien placée (revue 2026-08-20) — erreur de
    // retenue sur le calcul posé (P ± 10 ou P ± 100). Sans ce distracteur, les
    // 4 choix partageaient les chiffres de P et l'exercice se réduisait au
    // placement de la virgule (attendu 612) sans calcul réel.
    dist.add(shiftLeft(String(parseInt(P, 10) + randomChoice([10, -10, 100, -100])), v.decTot));

    const arr = [...dist].filter((x) => !sameDecimal(x, correct));
    while (arr.length < 3) {
        const extra = shiftLeft(P, v.decTot + arr.length + 2);
        if (!sameDecimal(extra, correct) && !arr.some((x) => sameDecimal(x, extra))) arr.push(extra);
        else arr.push(`${arr.length}`);
    }

    return makeQcm(
        [correct, ...arr.slice(0, 3)],
        correct,
        randomChoice(FORMULATIONS_QCM_PRODUIT)(v.aStr, v.bStr),
        opts(L_QCM),
    );
};

// ═══════════════════════════════════════════════════════════
// 2. SAISIE — Taper le résultat d'un produit décimal
// ═══════════════════════════════════════════════════════════

// Anti-répétition immédiate (revue 2026-08-20, recette standard).
let derniereSignatureSaisie613 = null;

const genCalcMultSaisie = () => {
    let payload = construireCalcMultSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureSaisie613; t++) {
        payload = construireCalcMultSaisie();
    }
    derniereSignatureSaisie613 = payload.texte;
    return payload;
};

const construireCalcMultSaisie = () => {
    const couple = randomChoice(COUPLES_ENTIERS);
    const v = randomDecline(couple, { minDec: 2 });
    return makeSaisie(
        v.resStr,
        `${RAPPEL_BROUILLON}<p>Calcule : ${v.aStr} × ${v.bStr} = ?</p>`,
        { ...opts(L_SAISIE), strict: false },
    );
};

// ═══════════════════════════════════════════════════════════
// 3. RELIER — 4 produits indépendants avec 4 résultats distincts
// ═══════════════════════════════════════════════════════════

const genCalcMultRelier = () => {
    const selected = [];
    const usedResults = new Set();
    const usedCouples = new Set();

    const tryAdd = (couple) => {
        const key = couple.join(',');
        if (usedCouples.has(key)) return false;
        const v = randomDecline(couple, { minDec: 2 });
        if (usedResults.has(v.resStr)) return false;
        usedCouples.add(key);
        usedResults.add(v.resStr);
        selected.push({
            source: `${v.aStr} × ${v.bStr}`,
            target: v.resStr,
        });
        return true;
    };

    let safety = 0;
    while (selected.length < 2 && safety < 60) {
        safety++;
        tryAdd(randomChoice(COUPLES_ENTIERS));
    }

    // Fallback déterministe
    if (selected.length < 2) {
        selected.length = 0;
        usedCouples.clear();
        usedResults.clear();
        // 2 produits aux résultats garantis distincts
        const fallback = [
            { source: '4,2 × 1,3', target: '5,46' },
            { source: '3,6 × 2,5', target: '9'    },
        ];
        fallback.forEach((p) => selected.push(p));
    }

    // Leurres (bilan prof D3, gabarit 2 sources / 4 cibles) : le résultat de
    // CHAQUE produit décalé d'un rang de virgule (code decalage_virgule) —
    // l'erreur de placement de la virgule, même famille que le format drag du
    // même attendu. Sens tiré par source ; repli : l'autre sens, puis 2 rangs.
    const pris = new Set(selected.map((p) => p.target));
    const leurres = [];
    for (const p of selected) {
        const sens = randomChoice([1, -1]);
        const candidats = [
            sens === 1 ? shiftLeftNegative(p.target, 1) : shiftLeft(p.target, 1),
            sens === 1 ? shiftLeft(p.target, 1) : shiftLeftNegative(p.target, 1),
            shiftLeftNegative(p.target, 2),
            shiftLeft(p.target, 2),
        ];
        for (const c of candidats) {
            if (!c || pris.has(c)) continue;
            pris.add(c);
            leurres.push({ target: c, code: 'decalage_virgule' });
            break;
        }
    }

    const payload = makeRelier(
        selected,
        `${RAPPEL_BROUILLON}<p>Relie chaque produit à son résultat. Attention : certaines étiquettes ne correspondent à aucun produit.</p>`,
        { ...opts(L_RELIER), cibles_leurres: leurres },
    );
    payload._meta.params = {
        distracteurs: Object.fromEntries(leurres.map((l) => [l.target, l.code])),
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 4. TABLEAU V/F — 6 lignes 3V/3F ou 4V/2F
// ═══════════════════════════════════════════════════════════

const genCalcMultVF = () => {
    const lignes = [];
    const bonnes = []; // 0 = Vrai, 1 = Faux
    const seen = new Set();

    const push = (text, isVrai) => {
        if (seen.has(text)) return false;
        seen.add(text);
        lignes.push(text);
        bonnes.push(isVrai ? 0 : 1);
        return true;
    };

    // Quota variable de lignes vraies (revue 2026-08-20) : 1 à 3 sur 4 lignes,
    // pour empêcher la stratégie de comptage « toujours 2 Vrai / 2 Faux ».
    const nbVraiCible = randInt(1, 3);
    const nbFauxCible = 4 - nbVraiCible;
    const nbFaux = () => bonnes.filter((b) => b === 1).length;

    // Lignes VRAIES (couples différents)
    const couplesShuffled = shuffle(COUPLES_ENTIERS);
    let idx = 0;
    while (lignes.filter((_, i) => bonnes[i] === 0).length < nbVraiCible && idx < couplesShuffled.length) {
        const v = randomDecline(couplesShuffled[idx++], { minDec: 2 });
        push(`${v.aStr} × ${v.bStr} = ${v.resStr}`, true);
    }

    // Lignes FAUSSES parmi 3 familles d'erreur (shuffle pour varier l'angle testé)
    const faussesFamilies = shuffle([
        // F1 : virgule absente (résultat = P entier brut)
        () => {
            for (let t = 0; t < 20; t++) {
                const couple = randomChoice(COUPLES_ENTIERS);
                const [, , P] = couple;
                const v = randomDecline(couple, { minDec: 2 });
                if (sameDecimal(P, v.resStr)) continue;
                if (push(`${v.aStr} × ${v.bStr} = ${P}`, false)) return;
            }
        },
        // F2 : décalage trop court d'un rang
        () => {
            for (let t = 0; t < 20; t++) {
                const couple = randomChoice(COUPLES_ENTIERS);
                const [, , P] = couple;
                const v = randomDecline(couple, { minDec: 2 });
                const faux = shiftLeft(P, Math.max(0, v.decTot - 1));
                if (sameDecimal(faux, v.resStr)) continue;
                if (push(`${v.aStr} × ${v.bStr} = ${faux}`, false)) return;
            }
        },
        // F3 : décalage trop long d'un rang
        () => {
            for (let t = 0; t < 20; t++) {
                const couple = randomChoice(COUPLES_ENTIERS);
                const [, , P] = couple;
                const v = randomDecline(couple, { minDec: 2 });
                const faux = shiftLeft(P, v.decTot + 1);
                if (sameDecimal(faux, v.resStr)) continue;
                if (push(`${v.aStr} × ${v.bStr} = ${faux}`, false)) return;
            }
        },
    ]);
    faussesFamilies.forEach((f) => { if (nbFaux() < nbFauxCible) f(); });

    // Équilibrage final (retente sur collision au lieu d'abandonner)
    let garde = 0;
    while (lignes.length < 4 && garde < 40) {
        garde++;
        const vraiCount = bonnes.filter((b) => b === 0).length;
        const couple = randomChoice(COUPLES_ENTIERS);
        const v = randomDecline(couple, { minDec: 2 });
        if (vraiCount < nbVraiCible) {
            push(`${v.aStr} × ${v.bStr} = ${v.resStr}`, true);
        } else {
            push(`${v.aStr} × ${v.bStr} = ${couple[2]}`, false);
        }
    }

    // Mélanger
    const indexed = lignes.map((l, i) => ({ l, b: bonnes[i] }));
    const shuf = shuffle(indexed);
    const finalLignes = shuf.map((x) => x.l);
    const finalBonnes = shuf.map((x) => x.b);

    return makeVraiFaux(
        finalLignes,
        finalBonnes,
        `${RAPPEL_BROUILLON}<p>Pour chaque égalité, dis si elle est vraie ou fausse.</p>`,
        opts(L_VF),
    );
};

// ═══════════════════════════════════════════════════════════
// 5. TEXTE À TROUS (DRAG) — 3 produits à compléter
// ═══════════════════════════════════════════════════════════

const genCalcMultTrouDrag = () => {
    // 3 produits indépendants avec résultats distincts
    const items = [];
    const usedResults = new Set();
    const usedCouples = new Set();

    const tryAdd = (couple) => {
        const key = couple.join(',');
        if (usedCouples.has(key)) return false;
        const v = randomDecline(couple, { minDec: 2 });
        if (usedResults.has(v.resStr)) return false;
        usedCouples.add(key);
        usedResults.add(v.resStr);
        items.push(v);
        return true;
    };

    let safety = 0;
    while (items.length < 3 && safety < 40) {
        safety++;
        tryAdd(randomChoice(COUPLES_ENTIERS));
    }

    // Fallback
    if (items.length < 3) {
        items.length = 0;
        items.push({ aStr: '3,4', bStr: '1,2', resStr: '4,08' });
        items.push({ aStr: '2,5', bStr: '0,6', resStr: '1,5'  });
        items.push({ aStr: '4,1', bStr: '2,3', resStr: '9,43' });
    }

    const phrase = items.map((v) => `${v.aStr} × ${v.bStr} = {{${v.resStr}}}`).join('\n');

    // Distracteurs : chaque bonne réponse décalée d'un rang à gauche/droite
    const dist = new Set();
    const bonnesSet = new Set(items.map((i) => i.resStr));
    const addIfNew = (x) => {
        if (x && !bonnesSet.has(x) && !dist.has(x)) dist.add(x);
    };

    // D1 : bonne1 × 10 (virgule décalée à droite)
    const d1 = shiftLeftNegative(items[0].resStr, 1);
    addIfNew(d1);
    // D2 : bonne2 × 10
    const d2 = shiftLeftNegative(items[1].resStr, 1);
    addIfNew(d2);
    // D3 : bonne3 × 10
    const d3 = shiftLeftNegative(items[2].resStr, 1);
    addIfNew(d3);

    // Fallback
    let safety2 = 0;
    while (dist.size < 3 && safety2 < 20) {
        safety2++;
        const rnd = randomChoice(COUPLES_ENTIERS);
        addIfNew(shiftLeft(rnd[2], randInt(0, 4)));
    }

    return makeTexteTrou(
        phrase,
        `${RAPPEL_BROUILLON}<p>Complète le tableau de produits en faisant glisser les bons résultats.</p>`,
        { ...opts(L_TROU_DRAG), mode: 'drag', distracteurs: [...dist].slice(0, 3), strict: false },
    );
};

/** Décale la virgule de k rangs vers la DROITE (×10^k). */
const shiftLeftNegative = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const oldDecs = decPart.length;
    const newDecs = oldDecs - k;
    if (newDecs >= 0) {
        const newInt = allDigits.slice(0, allDigits.length - newDecs) || '0';
        const newDec = allDigits.slice(allDigits.length - newDecs);
        const newIntTrim = newInt.replace(/^0+/, '') || '0';
        const newDecTrim = newDec.replace(/0+$/, '');
        if (newDecTrim === '') return newIntTrim;
        return `${newIntTrim},${newDecTrim}`;
    }
    const zeros = '0'.repeat(-newDecs);
    const fullInt = allDigits + zeros;
    return fullInt.replace(/^0+/, '') || '0';
};

// ─── Registre ───
const GENERATORS = {
    calcmult_qcm_unique: genCalcMultQcm,
    calcmult_texte: genCalcMultSaisie,
    calcmult_relier: genCalcMultRelier,
    calcmult_tableau_vf: genCalcMultVF,
    calcmult_texte_trou_drag: genCalcMultTrouDrag,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'calcmult', label: 'Calculer le produit de deux décimaux', formatsList: buildFormats('calcmult') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
